import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProfileDetails from './pages/ProfileDetails';
import SnapshotViewer from './pages/SnapshotViewer';
import ComparisonTool from './pages/ComparisonTool';
import ManualCapture from './pages/ManualCapture';
import AdminViewer from './pages/AdminManagement';
import {
  FeedHome,
  FeedOverview,
  FeedRegions,
  FeedLocationDetail,
  FeedReports,
  FeedComingSoon,
} from './pages/feed';
import './App.css';


// Modern theme with custom colors and typography
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#8b9ef0',
      dark: '#4a5fd8',
    },
    secondary: {
      main: '#764ba2',
      light: '#9a6bb8',
      dark: '#5a3a7a',
    },
    background: {
      default: '#f8fafc',
      paper: 'rgba(255, 255, 255, 0.95)',
    },
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/profile/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProfileDetails />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/snapshot/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <SnapshotViewer />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/compare"
              element={
                <PrivateRoute>
                  <Layout>
                    <ComparisonTool />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/capture"
              element={
                <PrivateRoute>
                  <Layout>
                    <ManualCapture />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <Layout>
                    <AdminViewer />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            {/* Trusted Feed Routes */}
            <Route
              path="/feed"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedHome />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/overview"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedOverview />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/regions"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedRegions />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/regions/:regionId"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedRegions />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/locations/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedLocationDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/reports"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedReports />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/posting"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedComingSoon pageType="posting" />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/reviews"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedComingSoon pageType="reviews" />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/feed/account"
              element={
                <PrivateRoute>
                  <Layout>
                    <FeedComingSoon pageType="account" />
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
